import { Injectable, Renderer2, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ScriptService, TrackingService as TrackingServiceBase } from '@zero/shared/util-services';
import { SOURCE_POINT_VENDOR, SourcePointService } from '@zero/shared/util-source-point';
import { environment } from 'environments/environment';
import { distinctUntilChanged } from 'rxjs';
import { WindowService } from '../dom/window.service';

interface TrackData {
  location: string;
  page: string;
  title: string;
  hitType: string;
}

interface TrackEventData {
  hitType: string;
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
  nonInteraction: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TrackingService extends TrackingServiceBase {
  readonly #windowService = inject(WindowService);
  readonly #router = inject(Router);
  readonly #sourcePoint = inject(SourcePointService);
  readonly #scriptService = inject(ScriptService);

  #trackingInitialized: boolean;
  #bufferedCookies = [];
  #bufferedUntilCookieConsent: (TrackData | TrackEventData)[] = [];
  readonly #MAX_EVENTS_TO_BUFFER = 10;

  #extractDocumentDomain(docDomain: string): string {
    let cookieDomain = docDomain;
    if (cookieDomain.split('.').length < 3 && cookieDomain !== 'localhost') {
      cookieDomain = `.${cookieDomain}`;
    }
    return cookieDomain;
  }

  #isExcludedFromTracking(path: string): boolean {
    if (!path) {
      return false;
    }
    return path.startsWith('/meindepot') || path.startsWith('/posteingang') || path.startsWith('/verwaltung');
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  public gaFn: Function = () => {
    // just empty
  };

  public init(renderer: Renderer2) {
    this.#sourcePoint
      .hasAccepted(SOURCE_POINT_VENDOR.GOOGLE_ANALYTICS)
      .pipe(distinctUntilChanged())
      .subscribe((accepted) => {
        if (!accepted) {
          this.#deleteGACookies();
          this.gaOptOut(true);
        } else {
          this.#scriptService.injectScript('assets/measure/measure.js', renderer).then(() => {
            this.#restoreBufferedGACookies();
            this.flushBufferedEvents();
            this.gaOptOut(false);
          });
        }
      });
  }

  public track(routerEvent: NavigationEnd): void {
    const trackData = this.createTrackData(routerEvent);
    if (trackData) {
      this.doTrack(trackData);
    }
  }

  public trackEvent(action: string, category: string, label?: string, nonInteraction = false): void {
    this.gaFn('set', 'location', this.getCurrentLocation(this.#router.url));
    const trackEventData = {
      hitType: 'event',
      eventCategory: category,
      eventAction: action,
      eventLabel: label || 'none',
      nonInteraction,
    };
    this.doTrack(trackEventData);
  }

  public doTrack(trackData: TrackData | TrackEventData): void {
    try {
      this.#initTracking();
      if (this.#trackingInitialized !== true) {
        return;
      }
      trackData['anonymizeIp'] = true;
      this.gaFn('send', trackData);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  #initTracking(): void {
    if (this.#trackingInitialized !== true && environment.googleAnalyticsTrackingId) {
      const ga = (this.#windowService.getWindow() as any).ga;
      if (typeof ga === 'function') {
        this.gaFn = ga;
        this.gaFn('create', environment.googleAnalyticsTrackingId, 'auto', {
          allowLinker: true,
        });
        this.gaFn('require', 'linker');
        this.gaFn('linker:autoLink', ['finanzen.net']);
        this.gaFn('set', 'anonymizeIp', true);
        this.gaFn('set', 'forceSSL', true);
        this.#trackingInitialized = true;
      }
    }
  }

  public extractTrackingUrl(url: string, removeAllQueryParams: boolean): string {
    const urlParts = url.split('?');
    const urlPath = urlParts[0];
    const urlParams = urlParts[1] ? urlParts[1].split('&') : [];
    let result = urlPath;
    const campaignParams = urlParams.filter((param) => param.startsWith('utm_'));

    if (campaignParams.length > 0 && removeAllQueryParams !== true) {
      result = `${result}?${campaignParams.join('&')}`;
    }

    return result;
  }

  #deleteGACookies(): void {
    const doc = this.#windowService.getWindow().document;
    const cookiesToBeBuffered = doc.cookie
      .split(';')
      .map((c) => c.trim())
      .filter((c) => c.startsWith('_ga=') || c.startsWith('_gid=') || c.startsWith('_gat_gtag_') || c.startsWith('_gat='));

    cookiesToBeBuffered.forEach((c) => {
      const cookieName = c.split('=')[0];
      const oldEntry = this.#bufferedCookies.findIndex((oldCookie) => oldCookie.startsWith(`${cookieName}=`));
      if (oldEntry > -1) {
        this.#bufferedCookies.splice(oldEntry, 1);
      }
    });
    this.#bufferedCookies.push(...cookiesToBeBuffered);

    const cookieDomain = this.#extractDocumentDomain(doc.domain);
    this.#bufferedCookies.forEach((c) => {
      doc.cookie = `${c};domain=${cookieDomain};expires=${new Date(0)}`;
    });
  }

  #restoreBufferedGACookies(): void {
    const doc = this.#windowService.getWindow().document;
    const cookieDomain = this.#extractDocumentDomain(doc.domain);

    if (this.#bufferedCookies) {
      this.#bufferedCookies.forEach((c) => {
        let exp = 63072000 * 1000; // GA default
        if (c.startsWith('_gid=')) {
          exp = 86400 * 1000;
        } else if (c.startsWith('_gat_gtag_')) {
          return;
        } else if (c.startsWith('_gat=')) {
          return;
        }
        doc.cookie = `${c};domain=${cookieDomain};expires=${new Date(new Date().getTime() + exp)}`;
      });

      this.#bufferedCookies = [];
    }
  }

  public bufferEvent(trackData: TrackData | TrackEventData): void {
    if (this.#bufferedUntilCookieConsent.length < this.#MAX_EVENTS_TO_BUFFER) {
      this.#bufferedUntilCookieConsent.push(trackData);
    }
  }

  public flushBufferedEvents(): void {
    this.#bufferedUntilCookieConsent.forEach(this.doTrack.bind(this));
    this.#bufferedUntilCookieConsent = [];
  }

  public createTrackData(routerEvent: NavigationEnd): TrackData {
    const path = this.extractTrackingUrl(routerEvent.url, true);
    if (this.#isExcludedFromTracking(path)) {
      return undefined;
    }

    const title = this.getCurrentTitle();
    const location = this.getCurrentLocation(routerEvent.url);
    return {
      hitType: 'pageview',
      location: location,
      page: path,
      title: title,
    } as TrackData;
  }

  public getCurrentTitle(): string {
    const doc = this.#windowService.getWindow().document;
    if (doc) {
      return doc.title;
    } else {
      return '(no title)';
    }
  }

  public getCurrentLocation(urlString: string): string {
    const loc = this.#windowService.getWindow().location;
    const pathWithFilteredQueryStrings = this.extractTrackingUrl(urlString, false);
    if (loc?.origin && pathWithFilteredQueryStrings) {
      return loc.origin + pathWithFilteredQueryStrings;
    } else {
      return '(no location)';
    }
  }

  public gaOptOut(optOut: boolean): void {
    const win = this.#windowService.getWindow();
    if (!win) {
      return;
    }
    win[`ga-disable-${environment.googleAnalyticsTrackingId}`] = optOut;
  }
}
